import type {
  ClientCancellationLineDto,
  ClientCancellationLineQueueItemDto,
  ClientCancellationLineQueueQuery,
  ClientCancellationSuspensionPolicyDto,
  CreateClientCancellationLineRequest,
  PatchClientCancellationLineStatusRequest,
} from '../../types/clientCancellationLines';
import { todayIsoDateLocal } from '../../utils/clientCancellationLines/clientCancellationLinesDate';
import { CLIENT_CANCELLATION_SUSPENSION_BLOCK_MESSAGE } from '../../utils/clientCancellationLines/clientCancellationLinesLabels';
import { podeTransicionarClientCancellationStatus } from '../../utils/clientCancellationLines/clientCancellationLinesStatusRules';

const MOCK_CONTRACT_ID = 'contract-demo-001';
const MOCK_LINE_IDS = ['line-demo-1', 'line-demo-2', 'line-demo-3'];

let politicaNovosContratosAtiva = true;

const store: ClientCancellationLineQueueItemDto[] = [
  {
    id: 'ccl-mock-1',
    contractId: MOCK_CONTRACT_ID,
    contractLineId: MOCK_LINE_IDS[0],
    ddd: '11',
    numeroLinha: '988887777',
    reason: 'SemUso0a4Meses',
    plannedDate: todayIsoDateLocal(),
    notes: 'Cliente pediu cancelamento por telefone interno.',
    status: 'Agendado',
    statusHistory: [
      {
        status: 'Agendado',
        notes: 'Registro inicial',
        changedAtUtc: new Date().toISOString(),
        changedByUserName: 'Consultor Demo',
      },
    ],
    createdAtUtc: new Date().toISOString(),
    createdByUserName: 'Consultor Demo',
    contractNumber: 'CT-2026-001',
    clientId: 'client-mock-1',
    clientRazaoSocial: 'Empresa Demo Ltda',
    clientCnpj: '12345678000199',
    protocolId: 'proto-cancel-1056',
    protocolNumber: 'PV-CCL-1056',
  },
  {
    id: 'ccl-mock-2',
    contractId: MOCK_CONTRACT_ID,
    contractLineId: MOCK_LINE_IDS[1],
    ddd: '11',
    numeroLinha: '977776666',
    reason: 'Zerada',
    plannedDate: todayIsoDateLocal(),
    notes: 'Aguardando job diário gerar protocolo.',
    status: 'ClienteAvisado',
    statusHistory: [
      {
        status: 'Agendado',
        notes: 'Registro inicial',
        changedAtUtc: new Date().toISOString(),
        changedByUserName: 'Consultor Demo',
      },
      {
        status: 'ClienteAvisado',
        notes: 'Cliente contactado',
        changedAtUtc: new Date().toISOString(),
        changedByUserName: 'Pós-venda mock',
      },
    ],
    createdAtUtc: new Date().toISOString(),
    createdByUserName: 'Consultor Demo',
    contractNumber: 'CT-2026-001',
    clientId: 'client-mock-1',
    clientRazaoSocial: 'Empresa Demo Ltda',
    clientCnpj: '12345678000199',
  },
];

function findIndex(id: string) {
  return store.findIndex((x) => x.id === id);
}

export const clientCancellationLinesMockStore = {
  getSuspensionBlockStatus(): ClientCancellationSuspensionPolicyDto {
    return {
      blockNewSuspensions: politicaNovosContratosAtiva,
      suspensionCutoffDate: '2026-11-03',
      policyReferenceDateDescription:
        'Contratos novos (data de referência a partir de 03/11/2026) não usam suspensão — orientar cancelamento pelo 1056. Contratos anteriores continuam com suspensão normal.',
      message: CLIENT_CANCELLATION_SUSPENSION_BLOCK_MESSAGE,
    };
  },

  setSuspensionBlockActive(active: boolean) {
    politicaNovosContratosAtiva = active;
  },

  listByContract(contractId: string): ClientCancellationLineDto[] {
    return store.filter((x) => x.contractId === contractId).map(({ ...rest }) => rest);
  },

  create(contractId: string, payload: CreateClientCancellationLineRequest, lineLabel?: { ddd: string; numero: string }) {
    const id = `ccl-${Date.now()}`;
    const entry: ClientCancellationLineQueueItemDto = {
      id,
      contractId,
      contractLineId: payload.contractLineId,
      ddd: lineLabel?.ddd ?? '11',
      numeroLinha: lineLabel?.numero ?? '999999999',
      reason: payload.reason,
      plannedDate: payload.plannedDate,
      notes: payload.notes ?? null,
      status: 'Agendado',
      statusHistory: [
        {
          status: 'Agendado',
          notes: payload.notes ?? 'Agendado pelo consultor',
          changedAtUtc: new Date().toISOString(),
          changedByUserName: 'Usuário mock',
        },
      ],
      createdAtUtc: new Date().toISOString(),
      createdByUserName: 'Usuário mock',
      contractNumber: 'CT-MOCK',
      clientId: 'client-mock',
      clientRazaoSocial: 'Cliente Mock',
      clientCnpj: '00000000000000',
    };
    store.unshift(entry);
    return entry;
  },

  cancel(id: string): ClientCancellationLineDto {
    const idx = findIndex(id);
    if (idx < 0) throw new Error('Registro não encontrado');
    const item = store[idx];
    if (item.status !== 'Agendado') throw new Error('Somente entradas Agendado podem ser canceladas');
    item.status = 'Encerrado';
    item.statusHistory.push({
      status: 'Encerrado',
      notes: 'Cancelado pelo consultor',
      changedAtUtc: new Date().toISOString(),
      changedByUserName: 'Usuário mock',
    });
    return item;
  },

  patchStatus(id: string, payload: PatchClientCancellationLineStatusRequest): ClientCancellationLineDto {
    const idx = findIndex(id);
    if (idx < 0) throw new Error('Registro não encontrado');
    const item = store[idx];
    if (!podeTransicionarClientCancellationStatus(item.status, payload.status)) {
      throw new Error('Transição de status não permitida');
    }
    item.status = payload.status;
    if (payload.desfecho) {
      item.desfecho = payload.desfecho;
    }
    item.statusHistory.push({
      status: payload.status,
      notes: payload.notes ?? null,
      changedAtUtc: new Date().toISOString(),
      changedByUserName: 'Pós-venda mock',
    });
    return item;
  },

  queue(query: ClientCancellationLineQueueQuery): ClientCancellationLineQueueItemDto[] {
    const ref = query.referenceDate.slice(0, 10);
    return store.filter((item) => {
      const planned = item.plannedDate.slice(0, 10);
      if (query.includeOverdue) {
        return planned <= ref && item.status !== 'Encerrado';
      }
      return planned === ref && item.status !== 'Encerrado';
    });
  },
};

export function clientCancellationLinesUseMock(): boolean {
  return process.env.REACT_APP_CLIENT_CANCELLATION_LINES_USE_MOCK === 'true';
}
